import { useEffect, useState } from "react";
import { Plus, Trash2, Sparkles, Download, RefreshCw } from "lucide-react";
import api, { formatErr } from "../lib/api";

/* Wave V.6 — Interactive worksheet panels for L3 sub-sub-stages.
 *
 *   HazopWorksheet    — B.6.1
 *   SilLopaWorksheet  — B.6.2
 *   TbeWorksheet      — I.1.b
 *   LoopDiagramsPanel — F.3.c
 *   FireWaterPanel    — H.1.a
 */

const PANEL_BG = "var(--pf-surface)";
const CELL = { padding: "6px 10px", border: "1px solid var(--pf-border)", fontSize: 12, verticalAlign: "top" };
const HDR = { ...CELL, background: "var(--pf-bg)", fontWeight: 600, color: "var(--pf-spark)", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: "0.04em" };

/* ──────────────────────────── HAZOP ──────────────────────────── */
const HAZOP_PARAMS = ["Flow", "Pressure", "Temperature", "Level", "Composition", "Phase", "Corrosion", "Maintenance", "Start-up", "Shutdown"];
const HAZOP_GUIDE = ["No", "More", "Less", "Reverse", "Other Than", "As Well As", "Part Of"];

export function HazopWorksheet({ projectId }) {
  const [data, setData] = useState({ nodes: [], deviations: [], recommendations: [] });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ number: 1, name: "", pid_sheet: "", from_tag: "", to_tag: "", design_intent: "" });
  const [activeNode, setActiveNode] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/projects/${projectId}/worksheets/hazop`); setData(data); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const addNode = async () => {
    if (!form.name) return;
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/worksheets/hazop/nodes`, form);
      setForm({ ...form, number: form.number + 1, name: "", from_tag: "", to_tag: "", design_intent: "" });
      await load();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const delNode = async (id) => {
    await api.delete(`/projects/${projectId}/worksheets/hazop/nodes/${id}`);
    if (activeNode === id) setActiveNode(null);
    await load();
  };

  return (
    <div data-testid="hazop-worksheet" style={{ padding: 14, background: PANEL_BG, color: "var(--pf-text)" }}>
      <h3 style={{ fontSize: 16, marginBottom: 4 }}>HAZOP Study Worksheet (B.6.1)</h3>
      <p style={{ fontSize: 12, color: "var(--pf-text-muted)", marginBottom: 14 }}>
        Define nodes, walk through parameter × guide-word deviations, let Capexara Process Industry AI suggest causes / consequences / safeguards, and track recommendations.
      </p>
      {err && <div className="pf-sev pf-sev-error" style={{ padding: 8, marginBottom: 10 }}>{err}</div>}

      {/* ── Node register ── */}
      <h4 style={{ fontSize: 13, marginBottom: 6, color: "var(--pf-spark)" }}>Node Register</h4>
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10, fontSize: 12 }}>
        <thead>
          <tr>
            <th style={HDR}>#</th><th style={HDR}>Name</th><th style={HDR}>P&amp;ID</th>
            <th style={HDR}>From</th><th style={HDR}>To</th><th style={HDR}>Design Intent</th><th style={HDR}></th>
          </tr>
        </thead>
        <tbody>
          {data.nodes.map((n) => (
            <tr key={n.id}
                onClick={() => setActiveNode(n.id)}
                style={{ cursor: "pointer", background: activeNode === n.id ? "var(--pf-surface-2)" : "transparent" }}
                data-testid={`hazop-node-${n.number}`}>
              <td style={CELL}>{n.number}</td><td style={CELL}>{n.name}</td><td style={CELL}>{n.pid_sheet}</td>
              <td style={CELL}>{n.from_tag}</td><td style={CELL}>{n.to_tag}</td><td style={CELL}>{n.design_intent}</td>
              <td style={CELL}>
                <button onClick={(e) => { e.stopPropagation(); delNode(n.id); }} className="btn btn-ghost" style={{ padding: 4 }}
                        data-testid={`hazop-node-del-${n.number}`}>
                  <Trash2 size={12} />
                </button>
              </td>
            </tr>
          ))}
          <tr>
            <td style={CELL}><input className="pf-input" style={{ width: 40, fontSize: 11 }} type="number" value={form.number} onChange={(e) => setForm({ ...form, number: +e.target.value })} /></td>
            <td style={CELL}><input className="pf-input" style={{ width: "100%", fontSize: 11 }} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Feed Drum to Feed Pump" data-testid="hazop-node-name" /></td>
            <td style={CELL}><input className="pf-input" style={{ width: 90, fontSize: 11 }} value={form.pid_sheet} onChange={(e) => setForm({ ...form, pid_sheet: e.target.value })} placeholder="PID-100-001" /></td>
            <td style={CELL}><input className="pf-input" style={{ width: 70, fontSize: 11 }} value={form.from_tag} onChange={(e) => setForm({ ...form, from_tag: e.target.value })} placeholder="V-101" /></td>
            <td style={CELL}><input className="pf-input" style={{ width: 70, fontSize: 11 }} value={form.to_tag} onChange={(e) => setForm({ ...form, to_tag: e.target.value })} placeholder="P-101A" /></td>
            <td style={CELL}><input className="pf-input" style={{ width: "100%", fontSize: 11 }} value={form.design_intent} onChange={(e) => setForm({ ...form, design_intent: e.target.value })} placeholder="Transfer feed at 3.5 barg, 120°C" /></td>
            <td style={CELL}>
              <button onClick={addNode} disabled={busy} className="btn" style={{ padding: "4px 8px" }} data-testid="hazop-node-add">
                <Plus size={12} /> Add
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── Deviation matrix + worksheet for selected node ── */}
      {activeNode ? <DeviationMatrix projectId={projectId} nodeId={activeNode}
                                      deviations={data.deviations.filter((d) => d.node_id === activeNode)}
                                      onReload={load} /> : (
        <div style={{ padding: 20, textAlign: "center", color: "var(--pf-text-muted)", fontSize: 12,
                      border: "1px dashed var(--pf-border)", borderRadius: 6 }}>
          Select a node row above to open the deviation matrix.
        </div>
      )}

      {/* ── Recommendations register ── */}
      <h4 style={{ fontSize: 13, margin: "16px 0 6px", color: "var(--pf-spark)" }}>Recommendations Register</h4>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }} data-testid="hazop-rec-register">
        <thead>
          <tr><th style={HDR}>#</th><th style={HDR}>Node</th><th style={HDR}>Deviation</th><th style={HDR}>Recommendation</th><th style={HDR}>Priority</th><th style={HDR}>Status</th></tr>
        </thead>
        <tbody>
          {data.deviations.filter((d) => d.recommendation).map((d, i) => {
            const n = data.nodes.find((x) => x.id === d.node_id);
            return (
              <tr key={d.id}>
                <td style={CELL}>HR-{String(i + 1).padStart(3, "0")}</td>
                <td style={CELL}>{n ? `${n.number} · ${n.name}` : d.node_id}</td>
                <td style={CELL}>{d.parameter} · {d.guide_word}</td>
                <td style={CELL}>{d.recommendation}</td>
                <td style={CELL}>{d.risk_rank}</td>
                <td style={CELL}>{d.status}</td>
              </tr>
            );
          })}
          {data.deviations.filter((d) => d.recommendation).length === 0 && (
            <tr><td style={CELL} colSpan={6}>No recommendations yet — walk the deviations on each node.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function DeviationMatrix({ projectId, nodeId, deviations, onReload }) {
  const [open, setOpen] = useState(null);   // {parameter, guide_word}
  const [editing, setEditing] = useState(null);  // deviation object
  const [busy, setBusy] = useState(false);

  const openCell = (p, g) => {
    const existing = deviations.find((d) => d.parameter === p && d.guide_word === g);
    setOpen({ parameter: p, guide_word: g });
    setEditing(existing || {
      node_id: nodeId, parameter: p, guide_word: g,
      causes: "", consequences: "", safeguards: "", severity: 3, likelihood: 2,
      recommendation: "", action_by: "", due_date: "", status: "Open",
    });
  };

  const aiSuggest = async () => {
    if (!open) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/worksheets/hazop/ai-suggest`, {
        node_id: nodeId, parameter: open.parameter, guide_word: open.guide_word,
      });
      setEditing({ ...editing, ...data });
    } catch (e) { console.warn("AI suggest failed:", e?.message); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/worksheets/hazop/deviations`, editing);
      onReload();
      setOpen(null); setEditing(null);
    } catch (e) { console.warn(e); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="hazop-deviation-matrix">
      <h4 style={{ fontSize: 13, margin: "12px 0 6px", color: "var(--pf-spark)" }}>Deviation Matrix · Node selected</h4>
      <table style={{ borderCollapse: "collapse", fontSize: 11, marginBottom: 10 }}>
        <thead>
          <tr>
            <th style={HDR}></th>
            {HAZOP_GUIDE.map((g) => <th key={g} style={HDR}>{g}</th>)}
          </tr>
        </thead>
        <tbody>
          {HAZOP_PARAMS.map((p) => (
            <tr key={p}>
              <td style={{ ...HDR, textAlign: "left" }}>{p}</td>
              {HAZOP_GUIDE.map((g) => {
                const d = deviations.find((x) => x.parameter === p && x.guide_word === g);
                return (
                  <td key={g}
                      onClick={() => openCell(p, g)}
                      data-testid={`hazop-cell-${p}-${g}`}
                      style={{ ...CELL, cursor: "pointer", textAlign: "center",
                               background: d ? (d.risk_rank === "High" ? "#EF4444" :
                                                d.risk_rank === "Medium" ? "#F59E0B" : "#10B981") : "transparent",
                               color: d ? "white" : "var(--pf-text-muted)",
                               fontWeight: d ? 600 : 400 }}>
                    {d ? d.risk_rank[0] : "+"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {open && editing && (
        <div data-testid="hazop-deviation-editor"
             style={{ border: "1px solid var(--pf-border)", borderRadius: 6, padding: 12,
                      background: "var(--pf-bg)", marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <h5 style={{ margin: 0, fontSize: 13 }}>{open.parameter} · {open.guide_word}</h5>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={aiSuggest} disabled={busy} className="btn" style={{ padding: "4px 8px" }}
                      data-testid="hazop-ai-suggest">
                <Sparkles size={12} /> AI Suggest
              </button>
              <button onClick={save} disabled={busy} className="btn btn-primary" style={{ padding: "4px 8px" }}
                      data-testid="hazop-deviation-save">
                Save
              </button>
              <button onClick={() => { setOpen(null); setEditing(null); }} className="btn btn-ghost" style={{ padding: "4px 8px" }}>
                Cancel
              </button>
            </div>
          </div>
          {["causes", "consequences", "safeguards", "recommendation"].map((k) => (
            <div key={k} style={{ marginBottom: 6 }}>
              <label style={{ fontSize: 11, color: "var(--pf-text-muted)", textTransform: "capitalize" }}>{k}</label>
              <textarea className="pf-input pf-textarea" rows={2} value={editing[k] || ""}
                        data-testid={`hazop-field-${k}`}
                        onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
                        style={{ fontSize: 11 }} />
            </div>
          ))}
          <div style={{ display: "flex", gap: 12 }}>
            <label style={{ fontSize: 11 }}>Severity
              <select className="pf-input" style={{ fontSize: 11 }} value={editing.severity}
                      onChange={(e) => setEditing({ ...editing, severity: +e.target.value })}>
                {[1, 2, 3, 4, 5].map((n) => <option key={n}>{n}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 11 }}>Likelihood
              <select className="pf-input" style={{ fontSize: 11 }} value={editing.likelihood}
                      onChange={(e) => setEditing({ ...editing, likelihood: +e.target.value })}>
                {[1, 2, 3, 4, 5].map((n) => <option key={n}>{n}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 11 }}>Status
              <select className="pf-input" style={{ fontSize: 11 }} value={editing.status}
                      onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                <option>Open</option><option>In Progress</option><option>Closed</option>
              </select>
            </label>
            {editing.ai_generated && <span style={{ fontSize: 10, color: "var(--pf-spark)", alignSelf: "center" }}>[AI]</span>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────── SIL / LOPA ──────────────────────────── */
export function SilLopaWorksheet({ projectId }) {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    sif_id: "SIF-001", description: "", from_hazop_rec: "",
    initiating_event: "", ie_frequency_per_year: 0.1,
    consequence: "", consequence_category: "C3", target_frequency: 1e-4,
    ipls: [{ name: "Operator response (PFD 0.1)", pfd: 0.1 }],
    sif_architecture: "1oo1", proof_test_months: 12,
  });

  const load = async () => {
    try { const { data } = await api.get(`/projects/${projectId}/worksheets/sil`); setRows(data.rows); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const save = async () => {
    try {
      await api.post(`/projects/${projectId}/worksheets/sil`, form);
      await load();
      // Increment SIF id
      const next = "SIF-" + String(rows.length + 2).padStart(3, "0");
      setForm({ ...form, sif_id: next, description: "", initiating_event: "", consequence: "" });
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => { await api.delete(`/projects/${projectId}/worksheets/sil/${id}`); await load(); };

  const updateIpl = (i, v) => {
    const ipls = [...form.ipls];
    ipls[i] = { ...ipls[i], ...v };
    setForm({ ...form, ipls });
  };

  return (
    <div data-testid="sil-lopa-worksheet" style={{ padding: 14, background: PANEL_BG, color: "var(--pf-text)" }}>
      <h3 style={{ fontSize: 16, marginBottom: 4 }}>SIL / LOPA Assessment (B.6.2)</h3>
      <p style={{ fontSize: 12, color: "var(--pf-text-muted)", marginBottom: 12 }}>
        For each Safety Instrumented Function (SIF) identified from HAZOP, compute mitigated frequency and target SIL per IEC 61511.
      </p>
      {err && <div className="pf-sev pf-sev-error" style={{ padding: 8, marginBottom: 10 }}>{err}</div>}

      <h4 style={{ fontSize: 13, marginBottom: 6, color: "var(--pf-spark)" }}>SIF Register</h4>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginBottom: 14 }}>
        <thead>
          <tr>{["SIF ID","Description","From HAZOP","IE Freq/yr","Target","Mitigated","SIL Level","Arch","PT (mo)",""].map((h) => <th key={h} style={HDR}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`sil-row-${r.sif_id}`}>
              <td style={CELL}>{r.sif_id}</td><td style={CELL}>{r.description}</td><td style={CELL}>{r.from_hazop_rec}</td>
              <td style={CELL}>{r.ie_frequency_per_year}</td><td style={CELL}>{r.target_frequency}</td>
              <td style={CELL}>{(r.mitigated_frequency || 0).toExponential(2)}</td>
              <td style={{ ...CELL, fontWeight: 600, color: "var(--pf-spark)" }}>{r.sil_level}</td>
              <td style={CELL}>{r.sif_architecture}</td><td style={CELL}>{r.proof_test_months}</td>
              <td style={CELL}><button onClick={() => del(r.id)} className="btn btn-ghost" style={{ padding: 4 }}><Trash2 size={12} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>

      <h4 style={{ fontSize: 13, marginBottom: 6, color: "var(--pf-spark)" }}>Add New SIF</h4>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
        <label style={{ fontSize: 11 }}>SIF ID
          <input className="pf-input" value={form.sif_id} onChange={(e) => setForm({ ...form, sif_id: e.target.value })} data-testid="sil-form-id" />
        </label>
        <label style={{ fontSize: 11 }}>From HAZOP Rec
          <input className="pf-input" value={form.from_hazop_rec} onChange={(e) => setForm({ ...form, from_hazop_rec: e.target.value })} placeholder="HR-001" />
        </label>
        <label style={{ fontSize: 11, gridColumn: "span 2" }}>Description
          <input className="pf-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Trip pump P-101A on zero flow" data-testid="sil-form-description" />
        </label>
        <label style={{ fontSize: 11, gridColumn: "span 2" }}>Initiating Event
          <input className="pf-input" value={form.initiating_event} onChange={(e) => setForm({ ...form, initiating_event: e.target.value })} placeholder="Pump suction strainer blocks" />
        </label>
        <label style={{ fontSize: 11 }}>IE Frequency (per year)
          <input type="number" step="0.01" className="pf-input" value={form.ie_frequency_per_year} onChange={(e) => setForm({ ...form, ie_frequency_per_year: +e.target.value })} />
        </label>
        <label style={{ fontSize: 11 }}>Target Mitigated Frequency
          <input type="number" step="1e-5" className="pf-input" value={form.target_frequency} onChange={(e) => setForm({ ...form, target_frequency: +e.target.value })} />
        </label>
        <label style={{ fontSize: 11, gridColumn: "span 2" }}>Consequence
          <input className="pf-input" value={form.consequence} onChange={(e) => setForm({ ...form, consequence: e.target.value })} placeholder="Pump mechanical seal failure, hydrocarbon release" />
        </label>
      </div>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: "var(--pf-text-muted)", marginBottom: 4 }}>Independent Protection Layers (IPLs)</div>
        {form.ipls.map((ipl, i) => (
          <div key={i} style={{ display: "flex", gap: 6, marginBottom: 4 }}>
            <input className="pf-input" style={{ flex: 1 }} value={ipl.name} onChange={(e) => updateIpl(i, { name: e.target.value })} placeholder="IPL name" />
            <input type="number" step="0.01" className="pf-input" style={{ width: 80 }} value={ipl.pfd} onChange={(e) => updateIpl(i, { pfd: +e.target.value })} placeholder="PFD" />
            <button className="btn btn-ghost" onClick={() => setForm({ ...form, ipls: form.ipls.filter((_, j) => j !== i) })}><Trash2 size={12} /></button>
          </div>
        ))}
        <button className="btn btn-ghost" onClick={() => setForm({ ...form, ipls: [...form.ipls, { name: "", pfd: 0.1 }] })} style={{ fontSize: 11 }}>
          <Plus size={12} /> Add IPL
        </button>
      </div>
      <button className="btn btn-primary" onClick={save} data-testid="sil-save">Save SIF &amp; Compute SIL</button>
    </div>
  );
}

/* ──────────────────────────── TBE ──────────────────────────── */
export function TbeWorksheet({ projectId }) {
  const [entries, setEntries] = useState([]);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/projects/${projectId}/worksheets/tbe`); setEntries(data.entries); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const newEntry = () => setEditing({
    equipment_tag: "P-101A", service: "Feed pump",
    weights: { technical: 40, commercial: 30, delivery: 15, experience: 10, docs: 5 },
    vendors: [
      { name: "Vendor A", quote_price_inr: 4500000, delivery_weeks: 16, warranty_months: 18,
        extracted: { technical_score: 85, experience_score: 80, docs_score: 90 } },
      { name: "Vendor B", quote_price_inr: 3800000, delivery_weeks: 12, warranty_months: 12,
        extracted: { technical_score: 75, experience_score: 60, docs_score: 70 } },
    ],
  });

  const save = async () => {
    try { const { data } = await api.post(`/projects/${projectId}/worksheets/tbe`, editing); setEditing(data); await load(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  };

  const addVendor = () => setEditing({
    ...editing,
    vendors: [...editing.vendors, { name: `Vendor ${String.fromCharCode(65 + editing.vendors.length)}`, quote_price_inr: 0, delivery_weeks: 16, warranty_months: 12, extracted: { technical_score: 80, experience_score: 75, docs_score: 80 } }],
  });

  const updateVendor = (i, patch) => {
    const vendors = [...editing.vendors];
    vendors[i] = { ...vendors[i], ...patch };
    if ("technical_score" in patch || "experience_score" in patch || "docs_score" in patch) {
      vendors[i].extracted = { ...vendors[i].extracted, ...patch };
    }
    setEditing({ ...editing, vendors });
  };

  return (
    <div data-testid="tbe-worksheet" style={{ padding: 14, background: PANEL_BG, color: "var(--pf-text)" }}>
      <h3 style={{ fontSize: 16, marginBottom: 4 }}>Technical Bid Evaluation (I.1.b)</h3>
      <p style={{ fontSize: 12, color: "var(--pf-text-muted)", marginBottom: 12 }}>
        Score each vendor on technical compliance, commercial, delivery, experience, and documentation; Capexara Process Industry ranks them against your weights.
      </p>
      {err && <div className="pf-sev pf-sev-error" style={{ padding: 8, marginBottom: 10 }}>{err}</div>}

      {!editing && (
        <>
          <button className="btn btn-primary" onClick={newEntry} data-testid="tbe-new">
            <Plus size={12} /> New TBE
          </button>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 10, fontSize: 12 }}>
            <thead><tr>{["Equipment", "Service", "Vendors", "Recommended", ""].map((h) => <th key={h} style={HDR}>{h}</th>)}</tr></thead>
            <tbody>
              {entries.map((e) => {
                const rec = (e.vendors || []).find((v) => v.recommended);
                return (
                  <tr key={e.id}>
                    <td style={CELL}>{e.equipment_tag}</td><td style={CELL}>{e.service}</td>
                    <td style={CELL}>{(e.vendors || []).length}</td>
                    <td style={CELL}>{rec ? `★ ${rec.name} (${rec.scores.total})` : "—"}</td>
                    <td style={CELL}><button className="btn btn-ghost" onClick={() => setEditing(e)}>Edit</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {editing && (
        <div data-testid="tbe-editor">
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <label style={{ fontSize: 11 }}>Equipment Tag
              <input className="pf-input" value={editing.equipment_tag} onChange={(e) => setEditing({ ...editing, equipment_tag: e.target.value })} data-testid="tbe-tag" />
            </label>
            <label style={{ fontSize: 11 }}>Service
              <input className="pf-input" value={editing.service} onChange={(e) => setEditing({ ...editing, service: e.target.value })} />
            </label>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead><tr>{["Vendor", "Price INR", "Tech", "Comm", "Delivery wk", "Exp", "Docs", "Total", "Rec", ""].map((h) => <th key={h} style={HDR}>{h}</th>)}</tr></thead>
            <tbody>
              {editing.vendors.map((v, i) => (
                <tr key={i} data-testid={`tbe-vendor-${i}`} style={{ background: v.recommended ? "rgba(255,140,0,0.1)" : "transparent" }}>
                  <td style={CELL}><input className="pf-input" style={{ width: "100%", fontSize: 11 }} value={v.name} onChange={(e) => updateVendor(i, { name: e.target.value })} /></td>
                  <td style={CELL}><input type="number" className="pf-input" style={{ width: 90, fontSize: 11 }} value={v.quote_price_inr} onChange={(e) => updateVendor(i, { quote_price_inr: +e.target.value })} /></td>
                  <td style={CELL}><input type="number" className="pf-input" style={{ width: 50, fontSize: 11 }} value={(v.extracted || {}).technical_score ?? 80} onChange={(e) => updateVendor(i, { technical_score: +e.target.value })} /></td>
                  <td style={CELL}>{(v.scores || {}).commercial ?? "—"}</td>
                  <td style={CELL}><input type="number" className="pf-input" style={{ width: 50, fontSize: 11 }} value={v.delivery_weeks} onChange={(e) => updateVendor(i, { delivery_weeks: +e.target.value })} /></td>
                  <td style={CELL}><input type="number" className="pf-input" style={{ width: 50, fontSize: 11 }} value={(v.extracted || {}).experience_score ?? 75} onChange={(e) => updateVendor(i, { experience_score: +e.target.value })} /></td>
                  <td style={CELL}><input type="number" className="pf-input" style={{ width: 50, fontSize: 11 }} value={(v.extracted || {}).docs_score ?? 80} onChange={(e) => updateVendor(i, { docs_score: +e.target.value })} /></td>
                  <td style={{ ...CELL, fontWeight: 700, color: "var(--pf-spark)" }}>{(v.scores || {}).total ?? "—"}</td>
                  <td style={CELL}>{v.recommended ? "★" : ""}</td>
                  <td style={CELL}><button className="btn btn-ghost" onClick={() => setEditing({ ...editing, vendors: editing.vendors.filter((_, j) => j !== i) })}><Trash2 size={12} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            <button className="btn btn-ghost" onClick={addVendor} data-testid="tbe-add-vendor"><Plus size={12} /> Add Vendor</button>
            <button className="btn btn-primary" onClick={save} data-testid="tbe-save">Save &amp; Score</button>
            <button className="btn btn-ghost" onClick={() => setEditing(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────── Loop Diagrams ──────────────────────────── */
export function LoopDiagramsPanel({ projectId }) {
  const [loops, setLoops] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/projects/${projectId}/worksheets/loops`); setLoops(data.loops); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const auto = async () => {
    setBusy(true);
    try { await api.post(`/projects/${projectId}/worksheets/loops/auto-generate`); await load(); }
    finally { setBusy(false); }
  };

  const update = async (loop, patch) => {
    const merged = { ...loop, ...patch };
    await api.post(`/projects/${projectId}/worksheets/loops`, merged);
    setLoops((ls) => ls.map((l) => (l.id === loop.id ? merged : l)));
  };

  const del = async (id) => { await api.delete(`/projects/${projectId}/worksheets/loops/${id}`); await load(); };

  return (
    <div data-testid="loop-diagrams-panel" style={{ padding: 14, background: PANEL_BG, color: "var(--pf-text)" }}>
      <h3 style={{ fontSize: 16, marginBottom: 4 }}>Instrument Loop Diagrams (F.3.c)</h3>
      <p style={{ fontSize: 12, color: "var(--pf-text-muted)", marginBottom: 12 }}>
        One loop diagram per instrument — field to DCS/SIS via JB → marshalling → I/O channel. Starter set auto-generated from common tags; edit inline.
      </p>
      {err && <div className="pf-sev pf-sev-error" style={{ padding: 8, marginBottom: 10 }}>{err}</div>}
      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        <button className="btn btn-primary" onClick={auto} disabled={busy} data-testid="loops-auto-generate">
          <Sparkles size={12} /> Auto-Generate Starter Loops
        </button>
        <button className="btn btn-ghost" onClick={load} disabled={busy}><RefreshCw size={12} /> Reload</button>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
        <thead>
          <tr>{["Loop ID","Tag","Type","Range","Cable","Cores","JB","Term","Marshalling","I/O Card","I/O Ch","Final Element",""].map((h) => <th key={h} style={HDR}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {loops.map((l) => (
            <tr key={l.id} data-testid={`loop-${l.tag}`}>
              <td style={{ ...CELL, fontFamily: "'JetBrains Mono', monospace", color: "var(--pf-spark)" }}>{l.loop_id}</td>
              <td style={CELL}>{l.tag}</td>
              <td style={CELL}>{l.type}</td>
              <td style={CELL}><input className="pf-input" style={{ width: 70, fontSize: 11 }} value={l.range} onChange={(e) => update(l, { range: e.target.value })} /></td>
              <td style={CELL}><input className="pf-input" style={{ width: 70, fontSize: 11 }} value={l.cable_number} onChange={(e) => update(l, { cable_number: e.target.value })} /></td>
              <td style={CELL}><input type="number" className="pf-input" style={{ width: 40, fontSize: 11 }} value={l.cable_cores} onChange={(e) => update(l, { cable_cores: +e.target.value })} /></td>
              <td style={CELL}><input className="pf-input" style={{ width: 60, fontSize: 11 }} value={l.jb_number} onChange={(e) => update(l, { jb_number: e.target.value })} /></td>
              <td style={CELL}><input className="pf-input" style={{ width: 50, fontSize: 11 }} value={l.jb_terminal} onChange={(e) => update(l, { jb_terminal: e.target.value })} /></td>
              <td style={CELL}><input className="pf-input" style={{ width: 70, fontSize: 11 }} value={l.marshalling_cabinet} onChange={(e) => update(l, { marshalling_cabinet: e.target.value })} /></td>
              <td style={CELL}><input className="pf-input" style={{ width: 55, fontSize: 11 }} value={l.io_card} onChange={(e) => update(l, { io_card: e.target.value })} /></td>
              <td style={CELL}><input className="pf-input" style={{ width: 50, fontSize: 11 }} value={l.io_channel} onChange={(e) => update(l, { io_channel: e.target.value })} /></td>
              <td style={CELL}>{l.final_element}</td>
              <td style={CELL}><button className="btn btn-ghost" onClick={() => del(l.id)}><Trash2 size={12} /></button></td>
            </tr>
          ))}
          {loops.length === 0 && <tr><td style={CELL} colSpan={13}>No loop diagrams yet — click <b>Auto-Generate Starter Loops</b> to seed a set.</td></tr>}
        </tbody>
      </table>
      <p style={{ fontSize: 11, color: "var(--pf-text-muted)", marginTop: 8 }}>
        Each row represents a complete loop diagram (field → JB → marshalling → I/O). Download of per-loop PDFs will be available as an add-on.
      </p>
    </div>
  );
}

/* ──────────────────────────── Fire Water ──────────────────────────── */
export function FireWaterPanel({ projectId }) {
  const [input, setInput] = useState({
    hazard_category: "HH1", largest_equipment_area_m2: 450,
    fire_water_density_lpm_per_m2: 10.2, cooling_water_density_lpm_per_m2: 3.0,
    supplementary_m3h: 400, duration_hours: 4, num_hydrants: 0, num_monitors: 0,
  });
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try { const { data } = await api.get(`/projects/${projectId}/worksheets/fire-water`); if (data && data.result) setResult(data); }
    catch (e) { /* no prior */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const compute = async () => {
    try { const { data } = await api.post(`/projects/${projectId}/worksheets/fire-water/sizing`, input); setResult(data); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="fire-water-panel" style={{ padding: 14, background: PANEL_BG, color: "var(--pf-text)" }}>
      <h3 style={{ fontSize: 16, marginBottom: 4 }}>Fire Water System Sizing (H.1.a)</h3>
      <p style={{ fontSize: 12, color: "var(--pf-text-muted)", marginBottom: 12 }}>
        OISD-116 / NFPA-based fire-water demand, pump sizing, storage volume, and ring-main placement.
      </p>
      {err && <div className="pf-sev pf-sev-error" style={{ padding: 8, marginBottom: 10 }}>{err}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        {[
          ["Hazard Category (HH1/HH2/HH3)", "hazard_category", "text"],
          ["Largest Equipment Area (m²)", "largest_equipment_area_m2", "number"],
          ["Fire Water Density (lpm/m²)", "fire_water_density_lpm_per_m2", "number"],
          ["Cooling Water Density (lpm/m²)", "cooling_water_density_lpm_per_m2", "number"],
          ["Supplementary (m³/h, monitors+foam)", "supplementary_m3h", "number"],
          ["Duration (hours)", "duration_hours", "number"],
        ].map(([label, key, type]) => (
          <label key={key} style={{ fontSize: 11 }}>
            {label}
            <input type={type} className="pf-input" value={input[key]} onChange={(e) => setInput({ ...input, [key]: type === "number" ? +e.target.value : e.target.value })} data-testid={`fw-${key}`} />
          </label>
        ))}
      </div>
      <button className="btn btn-primary" onClick={compute} data-testid="fw-compute">
        <Sparkles size={12} /> Compute Sizing
      </button>

      {result && result.result && (
        <div data-testid="fw-result" style={{ marginTop: 16, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
          {Object.entries(result.result).map(([k, v]) => (
            <div key={k} style={{ border: "1px solid var(--pf-border)", borderRadius: 6, padding: 10 }}>
              <div style={{ fontSize: 10, color: "var(--pf-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{k.replace(/_/g, " ")}</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--pf-spark)" }}>{typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default HazopWorksheet;
